import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Mail, MessageSquare, Star, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { supabase } from '../services/supabase'

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    messageType: 'feedback' as 'feedback' | 'suggestion' | 'comment' | 'support',
    subject: '',
    message: '',
    rating: 0,
    isPublic: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            message_type: formData.messageType,
            subject: formData.subject,
            message: formData.message,
            rating: formData.rating > 0 ? formData.rating : null,
            is_public: formData.isPublic,
            status: 'pending'
          }
        ])
        .select()

      if (error) {
        console.error('Database error:', error)
        setSubmitStatus('error')
        
        // 提供更具体的错误信息
        if (error.code === 'PGRST116') {
          setErrorMessage('数据库表不存在，请联系管理员设置数据库')
        } else if (error.code === '42501') {
          setErrorMessage('权限不足，请联系管理员')
        } else if (error.message?.includes('404')) {
          setErrorMessage('数据库连接失败，请检查配置')
        } else {
          setErrorMessage('提交失败，请重试')
        }
      } else {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          messageType: 'feedback' as 'feedback' | 'suggestion' | 'comment' | 'support',
          subject: '',
          message: '',
          rating: 0,
          isPublic: false
        })
        console.log('Contact form submitted:', data[0])
      }
    } catch (error: any) {
      console.error('Submit error:', error)
      setSubmitStatus('error')
      setErrorMessage('网络错误，请重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Contact AI Decision Maker - Get Support & Feedback | Free Online Decision Assistant</title>
        <meta name="description" content="Contact AI Decision Maker for support, feedback, or questions about our free online decision assistant. Get help with AI decision making tools and spinning wheel features." />
        <meta name="keywords" content="contact ai decision maker, ai decision maker support, decision assistant help, ai decision maker feedback, decision tool contact" />
        <meta property="og:title" content="Contact AI Decision Maker - Get Support & Feedback" />
        <meta property="og:description" content="Contact AI Decision Maker for support, feedback, or questions about our free online decision assistant." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.org/contact" />
        <meta property="og:image" content="https://aidecision.org/contact-og-image.png" />
        <meta name="twitter:title" content="Contact AI Decision Maker - Get Support & Feedback" />
        <meta name="twitter:description" content="Contact AI Decision Maker for support, feedback, or questions about our free online decision assistant." />
        <meta name="twitter:image" content="https://aidecision.org/contact-twitter-image.png" />
        <link rel="canonical" href="https://aidecision.org/contact" />
        
        {/* Structured Data for Contact Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact AI Decision Maker",
            "description": "Contact page for AI Decision Maker - free online decision assistant",
            "url": "https://aidecision.org/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "AI Decision Maker",
              "email": "support@aidecision.org",
              "contactType": "customer service",
              "url": "https://aidecision.org"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold mb-6 text-gray-900">Contact AI Decision Maker</h1>
        <p className="mb-8 text-gray-700 text-lg">
          We value your feedback and are here to help! Please reach out with any questions, suggestions, or support needs about our AI decision maker and decision assistant tools.
        </p>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">Thank you for your feedback! We will get back to you soon.</span>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{errorMessage}</span>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Contact Information
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Email Support</h3>
                <p className="text-gray-700">support@aidecision.org</p>
                <p className="text-gray-600 text-sm mt-1">Get help with our AI decision maker tools and spinning wheel features</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Response Time</h3>
                <p className="text-gray-700">Within 24 hours</p>
                <p className="text-gray-600 text-sm mt-1">We typically respond to all inquiries within one business day</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Send us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="you@email.com"
                />
              </div>

              <div>
                <label htmlFor="messageType" className="block text-sm font-medium text-gray-700 mb-1">
                  Message Type
                </label>
                <select
                  id="messageType"
                  name="messageType"
                  value={formData.messageType}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="feedback">Feedback</option>
                  <option value="suggestion">Suggestion</option>
                  <option value="comment">Comment</option>
                  <option value="support">Support Request</option>
                </select>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Brief subject of your message"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your feedback, suggestion, or question about AI Decision Maker..."
                />
              </div>

              {/* Rating Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rate your experience (optional)
                </label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      className={`p-1 rounded ${
                        formData.rating >= star ? 'text-yellow-400' : 'text-gray-300'
                      } hover:text-yellow-400 transition-colors`}
                    >
                      <Star className="w-6 h-6 fill-current" />
                    </button>
                  ))}
                  {formData.rating > 0 && (
                    <span className="ml-2 text-sm text-gray-600">
                      {formData.rating} star{formData.rating !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Public Comment Option */}
              <div className="flex items-center">
                <input
                  id="isPublic"
                  name="isPublic"
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                  Allow us to use this as a public testimonial (we'll contact you first)
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="text-gray-500 text-sm mt-8 text-center">
          AI Decision Maker &copy; {new Date().getFullYear()}
        </p>
      </div>
    </>
  )
}

export default ContactPage 